import { FC, useState } from 'react';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import useUrlPreview from 'networking/mindfeed/hooks/useUrlPreview';
import { faviconOf, imageOf } from 'models/UrlPreview';

interface Props {
  // The site, shown straight away.
  host: string;
  // The address to fetch a preview for, once the timing hook has settled
  // on one. Undefined while the user is still typing.
  previewUrl?: string;
}

// The card under the link field: the site's name at once, then its page
// title, image and favicon once cortex has scraped them.
export const LinkPreviewCard: FC<Props> = ({ host, previewUrl }) => {
  const t = useTranslations();
  const killOpenGraphPreview = useFeatureIsOn(
    'kill-switch-disable-open-graph-preview',
  );
  const isEnabled = Boolean(previewUrl) && !killOpenGraphPreview;
  const { data, isSuccess, isFetching } = useUrlPreview(
    previewUrl ?? '',
    isEnabled,
  );
  const [isFaviconValid, setIsFaviconValid] = useState(true);

  const preview = isEnabled && isSuccess ? data : undefined;
  const image = imageOf(preview);
  const title = preview?.ogTitle?.trim();
  const favicon = faviconOf(preview);
  const showFavicon = Boolean(favicon) && isFaviconValid;
  const isLoading = isEnabled && isFetching && !preview;

  return (
    <div className={styles.container} data-testid="link-preview">
      {image ? (
        <img className={styles.image} src={image} alt="" />
      ) : (
        <span className={styles.letter} aria-hidden="true">
          {host.charAt(0).toUpperCase()}
        </span>
      )}

      <div className={styles.text}>
        {title ? (
          <>
            <span className={styles.title}>{title}</span>
            <span className={styles.siteRow}>
              {showFavicon ? (
                <img
                  className={styles.favicon}
                  src={favicon}
                  alt=""
                  onError={() => setIsFaviconValid(false)}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faLink}
                  className={styles.linkIcon}
                  aria-hidden="true"
                />
              )}
              <span className={styles.host}>{host}</span>
            </span>
          </>
        ) : (
          <>
            <span className={styles.title}>{host}</span>
            {isLoading && (
              <span className={styles.hint}>
                {t('memoComposer.link.loadingPreview')}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: classNames(
    'mt-3 flex items-center gap-3 rounded-xl border border-hairline bg-surface-card px-3 py-2.5 shadow-xs',
  ),
  image: 'size-12 shrink-0 rounded-lg bg-surface-field object-cover',
  letter:
    'flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-field text-sm font-bold text-ink-primary',
  text: 'flex min-w-0 flex-col gap-0.5',
  title: 'line-clamp-2 text-sm font-semibold text-ink-primary',
  siteRow: 'flex items-center gap-1.5',
  favicon: 'size-3.5 shrink-0 rounded-sm object-contain',
  linkIcon: 'text-[10px] text-ink-tertiary',
  host: 'truncate text-xs text-ink-secondary',
  hint: 'text-xs text-ink-secondary',
};
